import React, { createContext, useContext, useState, useEffect } from 'react';

interface PreScreeningData {
  ownsOrManagesProperty: boolean | null;
  hasPlannedSpace: boolean | null;
  hasHighSpeedInternet: boolean | null;
  acceptsNetworkPolicies: boolean | null;
}

interface VerificationData {
  hostName: string;
  emailAddress: string;
  phoneNumber: string;
  businessName: string;
  businessType: string;
  governmentId: File | null;
  businessLicense: File | null;
  address: string;
  neighborhoodDescription: string;
  agreesToTerms: boolean;
}

interface SubscriptionData {
  selectedPlan: string;
  securitySubscription: string;
  quantity: number;
}

interface PaymentData {
  financingOption: 'full' | '12months' | '24months' | '36months';
  installationOption: 'self' | 'professional';
}

interface OnboardingState {
  currentStep: number;
  preScreening: PreScreeningData;
  verification: VerificationData;
  subscription: SubscriptionData;
  payment: PaymentData;
}

interface OnboardingContextType {
  state: OnboardingState;
  updatePreScreening: (data: Partial<PreScreeningData>) => void;
  updateVerification: (data: Partial<VerificationData>) => void;
  updateSubscription: (data: Partial<SubscriptionData>) => void;
  updatePayment: (data: Partial<PaymentData>) => void;
  setCurrentStep: (step: number) => void;
  canProceedFromStep: (step: number) => boolean;
  resetOnboarding: () => void;
}

const initialState: OnboardingState = {
  currentStep: 1,
  preScreening: {
    ownsOrManagesProperty: null,
    hasPlannedSpace: null,
    hasHighSpeedInternet: null,
    acceptsNetworkPolicies: null,
  },
  verification: {
    hostName: '',
    emailAddress: '',
    phoneNumber: '',
    businessName: '',
    businessType: '',
    governmentId: null,
    businessLicense: null,
    address: '',
    neighborhoodDescription: '',
    agreesToTerms: false,
  },
  subscription: {
    selectedPlan: 'premium',
    securitySubscription: 'basic_adt',
    quantity: 1,
  },
  payment: {
    financingOption: 'full',
    installationOption: 'professional',
  },
};

const HostOnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useHostOnboarding = () => {
  const context = useContext(HostOnboardingContext);
  if (!context) {
    throw new Error('useHostOnboarding must be used within a HostOnboardingProvider');
  }
  return context;
};

interface HostOnboardingProviderProps {
  children: React.ReactNode;
}

export const HostOnboardingProvider = ({ children }: HostOnboardingProviderProps) => {
  const [state, setState] = useState<OnboardingState>(initialState);

  // Load state from localStorage on mount
  useEffect(() => {
    const loadStateFromStorage = () => {
      try {
        const step1Data = localStorage.getItem('hostOnboardingStep1');
        const step2Data = localStorage.getItem('hostOnboardingStep2');
        const step3Data = localStorage.getItem('hostOnboardingStep3');
        const step4Data = localStorage.getItem('hostOnboardingStep4');

        setState(prevState => ({
          ...prevState,
          preScreening: step1Data ? JSON.parse(step1Data) : prevState.preScreening,
          verification: step2Data ? { ...prevState.verification, ...JSON.parse(step2Data) } : prevState.verification,
          subscription: step3Data ? JSON.parse(step3Data) : prevState.subscription,
          payment: step4Data ? JSON.parse(step4Data) : prevState.payment,
        }));
      } catch (error) {
        console.error('Error loading onboarding state from localStorage:', error);
      }
    };

    loadStateFromStorage();
  }, []);

  const updatePreScreening = (data: Partial<PreScreeningData>) => {
    setState(prevState => ({
      ...prevState,
      preScreening: { ...prevState.preScreening, ...data }
    }));
    localStorage.setItem('hostOnboardingStep1', JSON.stringify({ ...state.preScreening, ...data }));
  };

  const updateVerification = (data: Partial<VerificationData>) => {
    setState(prevState => ({
      ...prevState,
      verification: { ...prevState.verification, ...data }
    }));
    localStorage.setItem('hostOnboardingStep2', JSON.stringify({ ...state.verification, ...data }));
  };

  const updateSubscription = (data: Partial<SubscriptionData>) => {
    setState(prevState => ({
      ...prevState,
      subscription: { ...prevState.subscription, ...data }
    }));
    localStorage.setItem('hostOnboardingStep3', JSON.stringify({ ...state.subscription, ...data }));
  };

  const updatePayment = (data: Partial<PaymentData>) => {
    setState(prevState => ({
      ...prevState,
      payment: { ...prevState.payment, ...data }
    }));
    localStorage.setItem('hostOnboardingStep4', JSON.stringify({ ...state.payment, ...data }));
  };

  const setCurrentStep = (step: number) => {
    setState(prevState => ({ ...prevState, currentStep: step }));
  };

  const canProceedFromStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return Object.values(state.preScreening).every(value => value === true);
      case 2:
        const requiredFields = [
          state.verification.hostName,
          state.verification.emailAddress,
          state.verification.phoneNumber,
          state.verification.businessName,
          state.verification.businessType,
          state.verification.address,
          state.verification.neighborhoodDescription,
        ];
        return requiredFields.every(field => field.trim() !== '') &&
               state.verification.governmentId !== null &&
               state.verification.businessLicense !== null &&
               state.verification.agreesToTerms;
      case 3:
        return Boolean(state.subscription.selectedPlan) && 
               Boolean(state.subscription.securitySubscription) && 
               state.subscription.quantity > 0;
      case 4:
        return true; // Final step, always can proceed
      default:
        return false;
    }
  };

  const resetOnboarding = () => {
    setState(initialState);
    localStorage.removeItem('hostOnboardingStep1');
    localStorage.removeItem('hostOnboardingStep2');
    localStorage.removeItem('hostOnboardingStep3');
    localStorage.removeItem('hostOnboardingStep4');
  };

  const contextValue: OnboardingContextType = {
    state,
    updatePreScreening,
    updateVerification,
    updateSubscription,
    updatePayment,
    setCurrentStep,
    canProceedFromStep,
    resetOnboarding,
  };

  return (
    <HostOnboardingContext.Provider value={contextValue}>
      {children}
    </HostOnboardingContext.Provider>
  );
};

export default HostOnboardingContext;
